import { skipToken, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CardPhase } from '../../../api/cards';
import { cardMessageText, getCardPreviewUrls } from '../../../api/cards';
import type { CardResult } from '../../../types';
import type { PreviewUrls } from './PreviewDialog';
import { PreviewDialog } from './PreviewDialog';

export function ResultsTable({
  results,
  jobId,
  phase,
}: {
  results: CardResult[];
  jobId: string | null;
  phase: CardPhase;
}) {
  const { t } = useTranslation();
  const [previewUpn, setPreviewUpn] = useState<string | null>(null);
  // One bulk call for the whole table — a request per card tripped the API's
  // inbound rate limit on large batches. Gated on the stream being finished:
  // while it runs, every result row pushed would re-key the fetch.
  //
  // Previews are decorative, so a failure is not retried and not surfaced —
  // the table still lists every outcome.
  const { data: presignedUrls = {} } = useQuery({
    queryKey: ['cards', 'previews', jobId] as const,
    // `skipToken` narrows for us: the fetch only exists once the job does.
    queryFn:
      phase === 'done' && jobId && results.some((r) => r.status === 'success')
        ? async () => {
            const previews = await getCardPreviewUrls(jobId);
            return Object.fromEntries(
              Object.entries(previews).map(([upn, p]) => [
                upn,
                { front: p.url, back: p.backUrl },
              ]),
            );
          }
        : skipToken,
    retry: false,
    staleTime: Infinity,
  });

  // The cards the dialog can show, in table order — what prev/next walk.
  const viewable = results.filter((r) => presignedUrls[r.upn] !== undefined);
  const previewCard = viewable.find((r) => r.upn === previewUpn);

  // The pre-flight stream only resolves recipients — no card exists yet, so
  // the column would just be a placeholder per row.
  const showCardColumn = phase !== 'preflighting';

  // Keep the newest row in view: every appended row scrolls the table to the
  // bottom, unconditionally. A scroll-position "pin" that released the follow
  // while the user read earlier rows proved unreliable — scroll events arrive
  // asynchronously, so after a burst of SSE rows the handler measured the
  // already-taller content and dropped the follow for the rest of the run.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [results.length]);

  return (
    <>
      <div className="overflow-hidden border border-rule">
        <div ref={scrollRef} className="epi-table-scroll">
          <table className="epi-table w-full">
            <thead>
              <tr>
                {showCardColumn && <th>{t('cardGenerator.results.card')}</th>}
                <th>{t('cardGenerator.results.upn')}</th>
                <th>{t('cardGenerator.results.name')}</th>
                <th>{t('cardGenerator.results.status')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <ResultRow
                  key={r.upn}
                  result={r}
                  urls={presignedUrls[r.upn]}
                  showCard={showCardColumn}
                  onPreview={setPreviewUpn}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {previewCard && (
        <PreviewDialog
          upn={previewCard.upn}
          viewable={viewable}
          urlsByUpn={presignedUrls}
          onShow={setPreviewUpn}
          onClose={() => setPreviewUpn(null)}
        />
      )}
    </>
  );
}

interface ResultRowProps {
  result: CardResult;
  /** Presigned preview URLs for this card, once the bulk fetch has landed. */
  urls?: PreviewUrls;
  /** Whether the table has a card column at all (not during pre-flight). */
  showCard: boolean;
  onPreview: (upn: string) => void;
}

/** Row thumbnail of one card side — recto and verso render identically. */
function CardThumb({ src, alt }: { src: string; alt: string }) {
  return (
    <img
      src={src}
      alt={alt}
      className="h-10 w-16 cursor-zoom-in border border-rule bg-card object-cover"
    />
  );
}

function statusBadge(t: TFunction, status: CardResult['status']) {
  const tagClass: Record<CardResult['status'], string> = {
    success: 'epi-tag green',
    error: 'epi-tag magenta',
    not_found: 'epi-tag magenta',
  };
  const labels: Record<CardResult['status'], string> = {
    success: t('common.ok'),
    error: t('common.error'),
    not_found: t('common.error'),
  };
  return <span className={tagClass[status]}>{labels[status]}</span>;
}

/**
 * Memoised: during a run the SSE stream appends one result per event, and
 * without the memo every already-rendered row re-renders per arriving card —
 * O(n²) row renders over a batch.
 */
const ResultRow = memo(function ResultRow({
  result: r,
  urls,
  showCard,
  onPreview,
}: ResultRowProps) {
  const { t } = useTranslation();
  return (
    <tr>
      {showCard && (
        <td>
          {r.status === 'success' && urls ? (
            <button
              type="button"
              onClick={() => onPreview(r.upn)}
              className="flex gap-1"
            >
              <CardThumb src={urls.front} alt={r.displayName ?? r.upn} />
              {urls.back && (
                <CardThumb
                  src={urls.back}
                  alt={`${r.displayName ?? r.upn} (verso)`}
                />
              )}
            </button>
          ) : (
            <div className="h-10 w-16 border border-rule bg-ink/5" />
          )}
        </td>
      )}
      <td className="max-w-[180px] truncate font-mono text-xs text-muted">
        {r.upn}
      </td>
      <td className="text-ink">{r.displayName ?? '—'}</td>
      <td>
        <div className="flex items-center gap-1.5">
          {statusBadge(t, r.status)}
          {cardMessageText(t, r) && (
            <span
              className="max-w-[160px] truncate text-xs text-magenta"
              title={cardMessageText(t, r)}
            >
              {cardMessageText(t, r)}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
});
